import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ptBR } from "date-fns/locale";
import useSelect from "../hooks/useSelect";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;

  label {
    margin-bottom: 5px;
    font-size: 0.8125rem;
    font-weight: 600;
  }

  .box-select {
    position: relative;
    display: flex;
    align-items: center;

    .icon {
      position: absolute;
      right: 20px;
      pointer-events: none;
    }
    .separator {
      position: absolute;
      height: 23px;
      width: 1px;
      background-color: #d9d9d9;
      right: 50px;
      top: 50%;
      transform: translateY(-50%);
    }

    .react-select__control {
      border-radius: 8px;
      border: 1px solid #d9d9d9;
      height: 49px;
      box-shadow: none;
      transition: border-color 0.2s ease-in-out;
      padding: 0 8px;
      display: flex;
      align-items: center;
    }

    .react-select__indicator-separator {
      display: none;
    }

    .react-select__indicator {
      display: none; /* Oculta o indicador (seta) */
    }

    .react-select__menu {
      z-index: 70;
    }

    .react-select__option {
      font-size: 0.8125rem;
      line-height: 2rem;
      padding: 8px 16px;
      cursor: pointer;
      &:hover {
        background-color: rgb(222, 235, 255);
        color: #192e49;
      }
    }

    .react-select__single-value {
      font-size: 0.8125rem;
      line-height: 2rem;
    }

    .react-select__placeholder {
      font-size: 0.8125rem;
      line-height: 2rem;
    }

    .error {
      color: #e53e3e;
      font-size: 12px;
      margin-top: 5px;
    }
  }

  .custom-datepicker {
    position: absolute;
    top: 60px;
    width: 100%;
    z-index: 80;
    display: flex;
    justify-content: center;

    .react-datepicker-wrapper {
      width: 100%;
    }
    .react-datepicker__day--disabled {
      opacity: 0.2;
    }
    label {
      margin-left: 4px;
      font-weight: 500;
      font-size: 0.8125rem;
    }
    input {
      border-radius: 8px;
      border: 1px solid #d9d9d9;
      height: 50px;
      width: 100%;
      appearance: none;
      transition: 0.2s ease-in-out;
      margin-bottom: 12px;
      padding: 0 16px;
      &::placeholder {
        color: black;
        font-weight: bold;
        font-size: 0.8125rem;
        opacity: 0.6;
      }
    }
    .react-datepicker__header {
      background-color: #01054c !important;
      .react-datepicker__current-month {
        color: white !important;
        padding: 0.4rem 0;
      }
      .react-datepicker__day-names {
        background-color: #e9e9e9 !important;
        .react-datepicker__day-name {
          color: #000 !important;
        }
      }
    }
    .react-datepicker__navigation {
      top: 7px !important;
    }
    .react-datepicker__day--selected,
    .react-datepicker__day--in-range {
      background-color: #01054c !important;
      border-radius: 100% !important;
    }
    .react-datepicker__day {
      color: #01054c !important;
    }

    .react-datepicker__day--selected,
    .react-datepicker__day--in-selecting-range,
    .react-datepicker__day--in-range,
    .react-datepicker__month-text--selected,
    .react-datepicker__month-text--in-selecting-range,
    .react-datepicker__month-text--in-range,
    .react-datepicker__quarter-text--selected,
    .react-datepicker__quarter-text--in-selecting-range,
    .react-datepicker__quarter-text--in-range,
    .react-datepicker__year-text--selected,
    .react-datepicker__year-text--in-selecting-range,
    .react-datepicker__year-text--in-range {
      color: white !important;
      background-color: #01054c !important;
      border-radius: 100% !important;
    }
  }
`;

const customStyles = {
  option: (provided, state) => ({
    ...provided,
    color: "#000",
    backgroundColor: "inherit",
    "&:hover": {
      backgroundColor: state.isSelected ? "#01054C" : "#01054C",
      color: state.isSelected ? "#fff" : "#fff",
    },
  }),
  control: (provided) => ({
    ...provided,
    background: "#fff",
    borderColor: "#d9d9d9",
    borderRadius: "8px",
    minHeight: "49px",
    height: "49px",
    padding: "0 8px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "49px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: () => ({
    display: "none",
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#000",
    fontSize: "0.8125rem",
    lineHeight: "2rem",
    fontWeight: "bold",
    opacity: 0.6,
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#000",
    fontSize: "0.8125rem",
    lineHeight: "2rem",
    fontWeight: "bold",
  }),
};

const CustomSelectDatePicker = ({ filters, setFilters, periodo, type }) => {
  const options = [
    { label: "Hoje", value: "hoje" },
    { label: "Amanhã", value: "amanha" },
    { label: "Essa semana", value: "essa-semana" },
    { label: "Esse mês", value: "esse-mes" },
    { label: "Personalizado", value: "personalizado" },
  ];

  const filteredOptions = type
  ? options.filter(option => option.label !== "Amanhã")
  : options;
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [firstDate, setFirstDate] = useState(null);
  const [secondDate, setSecondDate] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    periodo.upDateValue("hoje");
    periodo.upDateDefaultValue({ label: "Hoje", value: "hoje" });
  }, []);

  const handleSelectChange = (option) => {
    periodo.upDateDefaultValue(option);
    periodo.upDateValue(option.value);
    if (option.value === "personalizado") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
      setFilters({ ...filters, periodo: option.value });
    }
  };

  function getDatesInRange(startDate, endDate) {
    if (!startDate || !endDate) return [];

    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start.getTime() > end.getTime()) {
      return [];
    }

    const dates = [];
    const msInDay = 24 * 60 * 60 * 1000;
    for (let t = start.getTime(); t <= end.getTime(); t += msInDay) {
      const d = new Date(t);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      dates.push(`${day}/${month}/${year}`);
    }
    return dates;
  }

  const handleDateChange = (date) => {
    if (Array.isArray(date)) {
      const [start, end] = date;
      setDateRange(date);
      if (start && !end) {
        setFirstDate(start);
      }
      if (start && end) {
        setSecondDate(end);
        const days = getDatesInRange(start, end);
        setFilters({ ...filters, periodo: days });
        setShowDatePicker(false);
      }
    }
  };

  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
      setShowDatePicker(false);
    }
  };

  useEffect(() => {
    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDatePicker]);

  return (
    <Wrapper ref={wrapperRef}>
      <label>Período:</label>
      <div className="box-select">
        <Select
          options={filteredOptions}
          onChange={handleSelectChange}
          placeholder="Selecione..."
          styles={customStyles}
          {...periodo}
        />
        <span className="icon">
          <svg width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M7 1.125L4 3.875L1 1.125"
              stroke="#01054C"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className="separator"></span>
        {showDatePicker && (
          <div className="custom-datepicker datapickerSelectCustom ">
            <DatePicker
              selectsRange
              startDate={dateRange[0]}
              endDate={dateRange[1]}
              onChange={handleDateChange}
              locale={ptBR}
              inline
              isClearable
            />
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default CustomSelectDatePicker;
